import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import { AppID } from "../../constants/provider/constant";
import { updateRequests, uploadFile } from "./common/apiCalls";
import { toast } from "react-toast";
import moment from "moment";
import { EVENTS } from "./events_constant";
import { logEventToAnalyticsPlatform } from "../../helper/events";

export default function DocumentUploadDialog({
  appointmentDate,
  requestId,
  speciality,
  patientName,
  contractId,
  docUploadDialogVisible,
  setDocUploadDialogVisibility,
  fetchData,
  setFetchData,
  currentProvider,
  pmEntityId,
}) {
  const [remark, setRemark] = useState("");

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const onFileUpload = async (event) => {
    const { files } = event.target;

    setUploadedFiles([...uploadedFiles, ...Object.values(files)]);
  };

  const removeFile = async (idx) => {
    const currentFiles = uploadedFiles.filter((x, i) => i !== idx);
    setUploadedFiles(currentFiles);
  };

  const uploadFilesAndUpdateRequest = async () => {
    try {
      const guids = {};
      for (const file of uploadedFiles) {
        const payload = new FormData();

        payload.append("fileName", file.name);
        payload.append("fileType", "Prescription");
        payload.append("file", file);
        payload.append("appId", AppID);
        payload.append("contractType", contractId);
        payload.append("s3AccountType", "MB");

        let response = await uploadFile(payload);
        if (response?.success && response?.data) {
          guids[Object.keys(response.data)[0]] = Object.values(response.data)[0];
        }
      }
      let attachFiles = {
        prescriptions: guids,
      };
      setDocUploadDialogVisibility(false);
      let updateResponse = await updateRequests(["OFFLINE_CONSULTS"], [requestId], remark, "UPLOAD_DOCUMENT", null, null, attachFiles);
      if (updateResponse?.success) {
        setFetchData(!fetchData);
        toast.success("Request updated successfully");

        logEventToAnalyticsPlatform({
          name: EVENTS.PC_LS_ACTION.name,
          payload: {
            tab_name: EVENTS.TAB_NAME_UPCOMING_APPOINTMENTS,
            filterValue: 'true',
            reference_id: '' + pmEntityId,
            reference_provider_id: '' + currentProvider,
            service_name: EVENTS.SERVICE_NAME_OFFLINE_CONSULTS,
            action: EVENTS.ACTION.DOCUMENT_UPLOAD_CLICKED,
          },
          schema: EVENTS.PC_LS_ACTION.schema,
        })

      } else {
        toast.error(`Request update failed - ${updateResponse?.error}`);
      }
    } catch (err) {
      toast.error(`Request update failed - ${err?.response?.data?.error}`);
    }
  };

  return (
    <Dialog open={docUploadDialogVisible} onClose={() => setDocUploadDialogVisibility(false)} fullWidth={true} maxWidth={"sm"}>
      <DialogTitle style={{ background: "rgb(245 245 245)" }}>
        <div className="flex justify-between">
          <div className="font-semibold">
            <span className="cursor-pointer">Upload Documents</span>
            <div className="text-xs text-gray-500">
              Request ID: <span className="font-semibold">{requestId} </span>
            </div>
            <div className="text-xs text-gray-500">
              Appointment Date and Time: <span className="font-semibold">{moment(appointmentDate).format("MMMM Do YYYY hh:mm A")} </span>
            </div>
            <div className="text-xs text-gray-500">
              Speciality: <span className="font-semibold">{speciality} </span>
            </div>
            <div className="text-xs text-gray-500">
              Patient Name: <span className="font-semibold">{patientName} </span>
            </div>
          </div>
          <div className="text-gray-500" onClick={() => setDocUploadDialogVisibility(false)}>
            <span className="cursor-pointer">
              <CloseIcon></CloseIcon>
            </span>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box className="mt-5">
          <div className="m-aut0 text-center border-dotted border-2 border-gray-400 rounded p-5">
            <label htmlFor="rx">
              <div>
                <FileUploadIcon></FileUploadIcon>
              </div>
              <div className="text-gray-500 text-xs font-semibold cursor-pointer">Upload Prescription</div>
            </label>
          </div>
          <div className="my-2 d-flex justify-content-center flex-column align-items-center">
            {uploadedFiles.map((fileDetails, idx) => (
              <div
                key={idx}
                className="my-2 py-1 px-2"
                style={{
                  backgroundColor: "#F6FFDF",
                  width: "fit-content",
                  position: "relative",
                }}
              >
                {fileDetails.name}
                <div style={{ position: "absolute", top: "-7px", right: "-7px" }} onClick={() => removeFile(idx)}>
                  <CancelIcon className="text-red-600 mx-2 cursor-pointer" style={{ fontSize: "15px" }}></CancelIcon>
                </div>
              </div>
            ))}
          </div>
          <input type="file" id="rx" className="hidden" multiple name="rx" onChange={(event) => onFileUpload(event)} />

          <div className="pb-10">
            <FormControl className="w-full">
              <TextField
                sx={{ fontSize: 14 }}
                id="remarks"
                label="Remarks*"
                multiline
                name="remarks"
                onChange={(event) => setRemark(event.target.value)}
                rows={4}
                value={remark}
              />
            </FormControl>
          </div>

          <div className="pb-5 flex justify-between text-center">
            <div
              className="border border-gray-600 rounded-md w-40 text-gray-900 p-1 text-sm cursor-pointer font-semibold"
              type="submit"
              variant="contained"
              size="large"
              onClick={() => setDocUploadDialogVisibility(false)}
            >
              <span>Cancel</span>
            </div>
            <button
              className={
                remark === "" || uploadedFiles?.length === 0
                  ? "border-gray-500 bg-gray-500 text-gray-100 border cursor-default rounded-md w-40 p-1 text-sm font-semibold"
                  : "border-blue-600 bg-blue-600 text-gray-100 border cursor-pointer rounded-md w-40 p-1 text-sm font-semibold"
              }
              type="submit"
              variant="contained"
              size="large"
              disabled={remark === "" || uploadedFiles?.length === 0}
              onClick={() => uploadFilesAndUpdateRequest()}
            >
              <span>Submit</span>
            </button>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
